/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import SpacerLogKpiCard from './SpacerLogKpiCard';
import SpacerLogKpiStrip from './SpacerLogKpiStrip';
import { fmtMinP, completionRail, shiftLabel, CHART_COLORS, BRAND_TEAL } from './spacerLogUtils';
import { baseChartOptions, gridScales } from './chartSetup';
import './chartSetup';

const DEFINITIONS_ROWS = [
  ['Fit-up', 'Pipe arrived → fit-up completed', 'Alignment and line-up ring problems show up here first'],
  ['ST1 weld', 'Fit-up completed → station 1 weld completed', 'The root pass — sets the pace for everything downstream'],
  ['Stations', 'ST1 weld completed → last station completed', 'Fill and cap; equipment faults land here'],
  ['Pull', 'Vessel start pulling → pulling completed', 'The move itself'],
  ['Cycle time', 'Pipe arrived → pulling completed', 'The single number a shift is judged on'],
  ['Shift median', 'Middle cycle time across all completed joints this shift', 'Resistant to one bad joint, unlike an average'],
  ['Status', 'Within 10% of median = on pace · 10–25% slower = watch · over 25% slower = over', 'Keeps colour meaningful rather than decorative'],
  ['Bottleneck', 'The stage furthest above its own shift median, for that joint', 'Turns a slow joint into a specific thing to fix'],
  ['Time lost', 'Every stage above its median, added up across the shift', 'The realistic size of the prize if the outliers are fixed'],
];

const timeOrDash = (val) => val || <span className="spacer-log-cell-muted">—</span>;

const SpacerLogDailyView = ({ data, shiftFilter, onShiftFilterChange, isLoading = false }) => {
  const [detailFilter, setDetailFilter] = useState('all');
  const { meta, compliance, kpis, jointProgression, charts, issueRows, jointRows } = data || {};

  const filteredJoints = useMemo(() => {
    let list = jointRows || [];
    if (detailFilter === 'day') list = list.filter((x) => x.shift === 0);
    if (detailFilter === 'night') list = list.filter((x) => x.shift === 1);
    if (detailFilter === 'incomplete') list = list.filter((x) => !x.isComplete);
    if (detailFilter === 'comments') list = list.filter((x) => x.comment);
    return list;
  }, [jointRows, detailFilter]);

  const kpiStripContent = useMemo(() => {
    if (!kpis) return null;
    const cr = kpis.completionRatePct ?? 0;
    const crRail = completionRail(cr);
    const dt = kpis.totalDowntimeMinutes ?? 0;
    const dtRail = dt > 120 ? 'bad' : dt > 60 ? 'warn' : 'good';
    const tl = kpis.timeLostMinutes ?? 0;
    const tlRail = tl > 90 ? 'bad' : tl > 40 ? 'warn' : 'good';

    return (
      <>
        <SpacerLogKpiCard
          label="Joints Completed"
          value={kpis.jointsCompleted ?? 0}
          sub={`of ${kpis.jointsLogged ?? 0} logged today`}
          rail="good"
          tag="On plan"
          tagClass="good"
        />
        <SpacerLogKpiCard
          label="Completion Rate"
          value={`${cr}%`}
          sub="day + night combined"
          rail={crRail}
          tag={cr >= 90 ? 'Healthy' : 'Watch'}
          tagClass={cr >= 90 ? 'good' : 'warn'}
        />
        <SpacerLogKpiCard
          label="Avg Cycle Time"
          value={`${kpis.avgCycleTimeMinutes ?? '—'} min`}
          sub={`shift median ${kpis.cycleMedianMinutes ?? '—'} min`}
          rail="neutral"
        />
        <SpacerLogKpiCard
          label="Time Lost"
          value={`${Math.round(tl)} min`}
          sub="stage-time above median, summed"
          rail={tlRail}
          tag={tlRail === 'good' ? 'Tight' : 'Recoverable'}
          tagClass={tlRail}
        />
        <SpacerLogKpiCard
          label="Total Downtime"
          value={fmtMinP(dt)}
          sub="summed across all joints"
          rail={dtRail}
          tag={dtRail === 'good' ? 'Low' : 'Elevated'}
          tagClass={dtRail}
        />
        <SpacerLogKpiCard
          label="Shifts Submitted"
          value={kpis.shiftsSubmitted || '—'}
          sub="day + night signed off"
          rail="good"
          tag="Closed"
          tagClass="good"
        />
        <SpacerLogKpiCard
          label="Report Status"
          value={kpis.reportStatusLabel || '—'}
          sub={compliance?.emailSent ? 'emailed to recipients' : 'report status'}
          rail="good"
          tag={compliance?.reportGenerated ? 'Distributed' : 'Pending'}
          tagClass={compliance?.reportGenerated ? 'good' : 'warn'}
        />
      </>
    );
  }, [kpis, compliance]);

  const completedCount = (jointProgression?.segments || []).filter((s) => s.complete).length;
  const totalRts =
    jointProgression?.lastRt && jointProgression?.firstRt
      ? jointProgression.lastRt - jointProgression.firstRt + 1
      : 0;

  const dayNightChart = useMemo(() => {
    const c = charts?.dayNightComparison;
    if (!c) return null;
    return {
      data: {
        labels: c.labels,
        datasets: [
          { label: 'Day', data: c.day, backgroundColor: CHART_COLORS.day, borderRadius: 3, maxBarThickness: 34 },
          { label: 'Night', data: c.night, backgroundColor: CHART_COLORS.night, borderRadius: 3, maxBarThickness: 34 },
        ],
      },
      options: {
        ...baseChartOptions,
        plugins: { legend: { position: 'top', align: 'end', labels: { boxWidth: 10, boxHeight: 10, color: CHART_COLORS.text } } },
        scales: gridScales,
      },
    };
  }, [charts]);

  const donutChart = useMemo(() => {
    const mix = charts?.completionMix;
    if (!mix) return null;
    return {
      data: {
        labels: ['Pull complete', 'Logged, not pulled'],
        datasets: [
          {
            data: [mix.completed, mix.incomplete],
            backgroundColor: [CHART_COLORS.primary, CHART_COLORS.warn],
            borderColor: '#FFFFFF',
            borderWidth: 3,
          },
        ],
      },
      options: {
        ...baseChartOptions,
        cutout: '68%',
        plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, boxHeight: 10, color: CHART_COLORS.text } } },
      },
    };
  }, [charts]);

  const stagesChart = useMemo(() => {
    const s = charts?.stageDurations;
    if (!s) return null;
    return {
      data: {
        labels: ['Fit-up', 'ST1 Weld', 'Stations', 'Pulling'],
        datasets: [
          {
            data: [s.fitup, s.weld, s.station, s.pull],
            backgroundColor: [CHART_COLORS.primary, CHART_COLORS.secondary, CHART_COLORS.warn, CHART_COLORS.orange],
            borderRadius: 3,
            maxBarThickness: 46,
          },
        ],
      },
      options: {
        ...baseChartOptions,
        indexAxis: 'y',
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { color: CHART_COLORS.grid }, ticks: { color: CHART_COLORS.tick }, title: { display: true, text: 'minutes', color: CHART_COLORS.tick } },
          y: { grid: { display: false } },
        },
      },
    };
  }, [charts]);

  const funnelChart = useMemo(() => {
    const funnel = charts?.funnel || [];
    if (!funnel.length) return null;
    return {
      data: {
        labels: funnel.map((f) => f.stage),
        datasets: [{ data: funnel.map((f) => f.count), backgroundColor: CHART_COLORS.primary, borderRadius: 3, maxBarThickness: 20 }],
      },
      options: {
        ...baseChartOptions,
        indexAxis: 'y',
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { color: CHART_COLORS.grid }, ticks: { color: CHART_COLORS.tick } },
          y: { grid: { display: false } },
        },
      },
    };
  }, [charts]);

  const downtimeChart = useMemo(() => {
    const flagged = charts?.downtimeByJoint || [];
    if (!flagged.length) return null;
    return {
      data: {
        labels: flagged.map((f) => `RT ${f.jointId} · ${shiftLabel(f.shift)}`),
        datasets: [
          {
            data: flagged.map((f) => f.downtimeMinutes),
            backgroundColor: flagged.map((f) => (f.shift === 0 ? CHART_COLORS.day : CHART_COLORS.night)),
            borderRadius: 3,
            maxBarThickness: 22,
          },
        ],
      },
      options: {
        ...baseChartOptions,
        indexAxis: 'y',
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { color: CHART_COLORS.grid }, ticks: { color: CHART_COLORS.tick }, title: { display: true, text: 'minutes', color: CHART_COLORS.tick } },
          y: { grid: { display: false } },
        },
      },
    };
  }, [charts]);

  useEffect(() => {
    if (shiftFilter === 'both') setDetailFilter('all');
    else if (shiftFilter === '0') setDetailFilter('day');
    else if (shiftFilter === '1') setDetailFilter('night');
  }, [shiftFilter]);

  return (
    <div>
      {kpiStripContent || isLoading ? (
        <SpacerLogKpiStrip isLoading={isLoading && !kpis}>{kpiStripContent}</SpacerLogKpiStrip>
      ) : null}

      <div className="spacer-log-panel">
        <div className="spacer-log-panel__head">
          <span className="spacer-log-panel__title">
            Joint Progression — RT {jointProgression?.firstRt ?? '—'} to {jointProgression?.lastRt ?? '—'}
          </span>
          <span className="spacer-log-panel__note">
            {completedCount} of {totalRts} RTs reached completion
          </span>
        </div>
        <div className="spacer-log-joint-strip-wrap">
          <div className="spacer-log-joint-strip">
            {(jointProgression?.segments || []).map((seg) => {
              let cls = 'spacer-log-joint-seg';
              if (seg.logged) cls += seg.complete ? ' spacer-log-joint-seg--complete' : ' spacer-log-joint-seg--incomplete';
              if (seg.flagged) cls += ' spacer-log-joint-seg--flag';
              return (
                <div
                  key={seg.jointId}
                  className={cls}
                  data-rt={seg.jointId}
                  title={`RT ${seg.jointId}${seg.logged ? '' : ' — not yet logged'}`}
                />
              );
            })}
          </div>
        </div>
        <div className="spacer-log-strip-legend">
          <div className="spacer-log-legend-item">
            <span className="spacer-log-legend-swatch" style={{ background: BRAND_TEAL }} /> Pull complete
          </div>
          <div className="spacer-log-legend-item">
            <span className="spacer-log-legend-swatch" style={{ background: '#b38600' }} /> Logged, not pulled
          </div>
          <div className="spacer-log-legend-item">
            <span className="spacer-log-legend-swatch spacer-log-legend-swatch--muted" /> RT not yet logged
          </div>
          <div className="spacer-log-legend-item" style={{ color: 'var(--sl-orange)' }}>
            ! joint flagged with a comment
          </div>
        </div>
      </div>

      <div className="spacer-log-row2">
        <div className="spacer-log-panel">
          <div className="spacer-log-panel__head">
            <span className="spacer-log-panel__title">Day vs Night Output</span>
            <span className="spacer-log-panel__note">grouped comparison</span>
          </div>
          <div className="spacer-log-chart-box">
            {dayNightChart ? <Bar data={dayNightChart.data} options={dayNightChart.options} /> : null}
          </div>
        </div>
        <div className="spacer-log-panel">
          <div className="spacer-log-panel__head">
            <span className="spacer-log-panel__title">Completion Mix</span>
            <span className="spacer-log-panel__note">{kpis?.jointsLogged ?? 0} joints logged</span>
          </div>
          <div className="spacer-log-chart-box">
            {donutChart ? <Doughnut data={donutChart.data} options={donutChart.options} /> : null}
          </div>
        </div>
      </div>

      <div className="spacer-log-row2">
        <div className="spacer-log-panel">
          <div className="spacer-log-panel__head">
            <span className="spacer-log-panel__title">Stage Duration Breakdown</span>
            <span className="spacer-log-panel__note">avg minutes / joint, completed only</span>
          </div>
          <div className="spacer-log-chart-box spacer-log-chart-box--tall">
            {stagesChart ? <Bar data={stagesChart.data} options={stagesChart.options} /> : null}
          </div>
        </div>
        <div className="spacer-log-panel">
          <div className="spacer-log-panel__head">
            <span className="spacer-log-panel__title">Milestone Funnel</span>
            <span className="spacer-log-panel__note">combined shifts</span>
          </div>
          <div className="spacer-log-chart-box spacer-log-chart-box--tall">
            {funnelChart ? <Bar data={funnelChart.data} options={funnelChart.options} /> : null}
          </div>
        </div>
      </div>

      <div className="spacer-log-panel">
        <div className="spacer-log-panel__head">
          <span className="spacer-log-panel__title">Downtime by Joint</span>
          <span className="spacer-log-panel__note">
            {fmtMinP(kpis?.totalDowntimeMinutes)} lost today across {(charts?.downtimeByJoint || []).length} joints
          </span>
        </div>
        <div className="spacer-log-chart-box">
          {downtimeChart ? <Bar data={downtimeChart.data} options={downtimeChart.options} /> : null}
        </div>
      </div>

      <div className="spacer-log-panel">
        <div className="spacer-log-panel__head">
          <span className="spacer-log-panel__title">Issues — Downtime &amp; Comments</span>
          <span className="spacer-log-panel__note">flagged joints only</span>
        </div>
        <div className="spacer-log-table-wrap spacer-log-scroll-table">
          <table className="spacer-log-table">
            <thead>
              <tr>
                <th>Shift</th>
                <th>Joint</th>
                <th>Downtime</th>
                <th>Last Milestone</th>
                <th>Comment</th>
              </tr>
            </thead>
            <tbody>
              {(issueRows || []).map((x) => (
                <tr key={`${x.shift}-${x.jointId}`}>
                  <td>
                    <span className={`spacer-log-badge spacer-log-badge--${x.shift === 0 ? 'day' : 'night'}`}>
                      {shiftLabel(x.shift)}
                    </span>
                  </td>
                  <td>RT {x.jointId}</td>
                  <td>{x.downtimeMinutes ? fmtMinP(x.downtimeMinutes) : <span className="spacer-log-cell-muted">—</span>}</td>
                  <td className="spacer-log-cell-muted">{x.lastMilestone}</td>
                  <td className="spacer-log-cell-comment">{x.comment || <span className="spacer-log-cell-muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="spacer-log-panel">
        <div className="spacer-log-panel__head">
          <span className="spacer-log-panel__title">Joint Detail — Drill Down</span>
          <span className="spacer-log-panel__note">
            {filteredJoints.length} row{filteredJoints.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="spacer-log-filter-row">
          {[
            { key: 'all', label: 'All' },
            { key: 'day', label: 'Day' },
            { key: 'night', label: 'Night' },
            { key: 'incomplete', label: 'Incomplete' },
            { key: 'comments', label: 'Has Comment' },
          ].map((f) => (
            <button
              key={f.key}
              type="button"
              className={`spacer-log-filter-btn${detailFilter === f.key ? ' spacer-log-filter-btn--active' : ''}`}
              onClick={() => setDetailFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="spacer-log-table-wrap spacer-log-scroll-table">
          <table className="spacer-log-table spacer-log-table--wide">
            <thead>
              <tr>
                <th>Shift</th>
                <th>RT</th>
                <th>Arrived</th>
                <th>Fit-up</th>
                <th>ST1 Weld</th>
                <th>Last Stn</th>
                <th>Pull Start</th>
                <th>Pull Done</th>
                <th>Cycle</th>
                <th>Status</th>
                <th>Bottleneck</th>
                <th>Downtime</th>
                <th>Comment</th>
              </tr>
            </thead>
            <tbody>
              {filteredJoints.map((x) => {
                const status = x.status;
                const bn = x.bottleneck;
                return (
                  <tr key={`${x.shift}-${x.jointId}`}>
                    <td>
                      <span className={`spacer-log-badge spacer-log-badge--${x.shift === 0 ? 'day' : 'night'}`}>
                        {shiftLabel(x.shift)}
                      </span>
                    </td>
                    <td>{x.jointId}</td>
                    <td>{timeOrDash(x.arrivedTime)}</td>
                    <td>{timeOrDash(x.fitUpTime)}</td>
                    <td>{timeOrDash(x.st1WeldTime || x.st1Time)}</td>
                    <td>
                      {x.lastStationTime || x.lastStnTime ? (
                        <>
                          {x.stationLabel ? `Stn ${x.stationLabel} @ ` : ''}
                          {x.lastStationTime || x.lastStnTime}
                        </>
                      ) : (
                        <span className="spacer-log-cell-muted">—</span>
                      )}
                    </td>
                    <td>{timeOrDash(x.pullStartTime)}</td>
                    <td>
                      {x.isComplete ? (
                        timeOrDash(x.pullDoneTime || x.pullingCompletedTime)
                      ) : (
                        <span className="spacer-log-cell-incomplete">In progress</span>
                      )}
                    </td>
                    <td>
                      {x.cycleTimeMinutes != null ? (
                        `${x.cycleTimeMinutes} min`
                      ) : (
                        <span className="spacer-log-cell-muted">—</span>
                      )}
                    </td>
                    <td>
                      {status?.level ? (
                        <span className={`spacer-log-status-pill spacer-log-status-pill--${status.level}`}>
                          {status.label}
                        </span>
                      ) : (
                        <span className="spacer-log-cell-muted">—</span>
                      )}
                    </td>
                    <td
                      className={`spacer-log-bottleneck-cell${bn?.overByMinutes > 0 ? ' spacer-log-bottleneck-cell--flagged' : ''}`}
                    >
                      {bn?.stage
                        ? `${bn.stage}${bn.overByMinutes > 0 ? ` +${bn.overByMinutes} min` : ''}`
                        : '—'}
                    </td>
                    <td>{x.downtimeMinutes ? fmtMinP(x.downtimeMinutes) : <span className="spacer-log-cell-muted">—</span>}</td>
                    <td className="spacer-log-cell-comment" style={{ fontFamily: 'var(--sl-sans)' }}>
                      {x.comment || ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="spacer-log-panel">
        <div className="spacer-log-panel__head">
          <span className="spacer-log-panel__title">How Each Number Is Worked Out</span>
          <span className="spacer-log-panel__note">so the dashboard and the shift report agree</span>
        </div>
        <div className="spacer-log-table-wrap spacer-log-scroll-table">
          <table className="spacer-log-table spacer-log-defs-table">
            <thead>
              <tr>
                <th>Measure</th>
                <th>Definition</th>
                <th>Why it matters</th>
              </tr>
            </thead>
            <tbody>
              {DEFINITIONS_ROWS.map(([m, d, w]) => (
                <tr key={m}>
                  <td>{m}</td>
                  <td>{d}</td>
                  <td>{w}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="spacer-log-panel">
        <div className="spacer-log-panel__head">
          <span className="spacer-log-panel__title">Compliance &amp; Record Status</span>
        </div>
        <div className="spacer-log-compliance">
          <div className="spacer-log-comp-item">
            <span className="spacer-log-comp-label">Day Shift</span>
            <span className="spacer-log-comp-value">
              <span className={`spacer-log-dot ${compliance?.daySubmitted ? 'spacer-log-dot--green' : 'spacer-log-dot--amber'}`} />
              {compliance?.daySkipped ? 'Skipped' : compliance?.daySubmitted ? 'Submitted' : 'Pending'}
            </span>
          </div>
          <div className="spacer-log-comp-item">
            <span className="spacer-log-comp-label">Night Shift</span>
            <span className="spacer-log-comp-value">
              <span className={`spacer-log-dot ${compliance?.nightSubmitted ? 'spacer-log-dot--green' : 'spacer-log-dot--amber'}`} />
              {compliance?.nightSkipped ? 'Skipped' : compliance?.nightSubmitted ? 'Submitted' : 'Pending'}
            </span>
          </div>
          <div className="spacer-log-comp-item">
            <span className="spacer-log-comp-label">PDF Report</span>
            <span className="spacer-log-comp-value">
              <span className={`spacer-log-dot ${compliance?.reportGenerated ? 'spacer-log-dot--green' : 'spacer-log-dot--amber'}`} />
              {compliance?.reportGenerated ? 'Generated' : 'Not generated'}
            </span>
            <span className="spacer-log-comp-sub">
              {compliance?.emailSent ? 'Emailed to recipients' : 'Not emailed'}
              {compliance?.reportDownloadUrl ? (
                <>
                  {' '}
                  —{' '}
                  <a className="spacer-log-pdf-link" href={compliance.reportDownloadUrl} target="_blank" rel="noreferrer">
                    open report
                  </a>
                </>
              ) : null}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpacerLogDailyView;
